import React, { memo } from 'react';
import FloatingWindow from './FloatingWindow';
import {
    ParticleCountChart,
    EnergyDistributionChart,
    VelocityHistogramChart,
    SpeciesPercentagesChart,
    EnergyGraphChart,
} from './charts';
import { useFloatingWindowsStore } from '../../store/floatingWindowsStore';

// Initial positions for each window type
const WINDOW_POSITIONS = {
    particleCount: { x: 100, y: 100 },
    energyDistribution: { x: 150, y: 120 },
    velocityHistogram: { x: 200, y: 140 },
    speciesPercentages: { x: 250, y: 160 },
    energyGraph: { x: 300, y: 180 },
};

const WINDOW_SIZES = {
    particleCount: { width: 650, height: 400 },
    energyDistribution: { width: 400, height: 350 },
    velocityHistogram: { width: 450, height: 350 },
    speciesPercentages: { width: 450, height: 400 },
    energyGraph: { width: 500, height: 350 },
};

/**
 * FloatingChartManager - Renders all floating chart windows.
 * This component should be rendered at App level so windows persist when sidebar closes.
 */
const FloatingChartManager: React.FC = memo(() => {
    const {
        particleCount,
        energyDistribution,
        velocityHistogram,
        speciesPercentages,
        energyGraph,
        closeWindow,
    } = useFloatingWindowsStore();

    return (
        <>
            {particleCount && (
                <FloatingWindow
                    id="particleCount"
                    title="📊 Particle Count"
                    isOpen={true}
                    onClose={() => closeWindow('particleCount')}
                    initialPosition={WINDOW_POSITIONS.particleCount}
                    initialSize={WINDOW_SIZES.particleCount}
                >
                    <ParticleCountChart />
                </FloatingWindow>
            )}

            {energyDistribution && (
                <FloatingWindow
                    id="energyDistribution"
                    title="⚡ Energy Distribution"
                    isOpen={true}
                    onClose={() => closeWindow('energyDistribution')}
                    initialPosition={WINDOW_POSITIONS.energyDistribution}
                    initialSize={WINDOW_SIZES.energyDistribution}
                >
                    <EnergyDistributionChart />
                </FloatingWindow>
            )}

            {velocityHistogram && (
                <FloatingWindow
                    id="velocityHistogram"
                    title="🎯 Velocity Histogram"
                    isOpen={true}
                    onClose={() => closeWindow('velocityHistogram')}
                    initialPosition={WINDOW_POSITIONS.velocityHistogram}
                    initialSize={WINDOW_SIZES.velocityHistogram}
                >
                    <VelocityHistogramChart />
                </FloatingWindow>
            )}

            {speciesPercentages && (
                <FloatingWindow
                    id="speciesPercentages"
                    title="📈 Species Percentages"
                    isOpen={true}
                    onClose={() => closeWindow('speciesPercentages')}
                    initialPosition={WINDOW_POSITIONS.speciesPercentages}
                    initialSize={WINDOW_SIZES.speciesPercentages}
                >
                    <SpeciesPercentagesChart />
                </FloatingWindow>
            )}

            {energyGraph && (
                <FloatingWindow
                    id="energyGraph"
                    title="📊 Energy Over Time"
                    isOpen={true}
                    onClose={() => closeWindow('energyGraph')}
                    initialPosition={WINDOW_POSITIONS.energyGraph}
                    initialSize={WINDOW_SIZES.energyGraph}
                >
                    <EnergyGraphChart />
                </FloatingWindow>
            )}
        </>
    );
});

FloatingChartManager.displayName = 'FloatingChartManager';

export default FloatingChartManager;
